import React, { useState } from "react";
import { Row, Col, Button, Nav, Tab } from "react-bootstrap";
import { useParams, useLocation } from "react-router";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PageTitle } from "domain/feature/hub/pageTitle/styles";
import { trans } from "sharedKernel/i18n";
import { trackService } from 'data/services/trackService';
import { Loading } from "components/loading";
import { 
  Wrapper, 
  SidebarCard, 
  LessonList, 
  LessonItem, 
  PlayBadge, 
  Sticky 
} from "../details/trackAvailableDetailsPage";

type Lesson = {
  id: string | number;
  title: string;
  minutes: number;
  index: number;
  videoId?: string;
  description?: string;
};

type SectionT = { 
  id: string | number; 
  title: string; 
  lessons: Lesson[] 
};

type TrackDetails = {
  id: string | number;
  title: string;
  coverUrl?: string;
  imageUrl?: string;
  totalMinutes: number;
  totalLessons: number;
  description: string[];
  sections: SectionT[];
};


export const TrackAvailableLessonPage: React.FC = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("visao-geral");
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  
  const lessonData = location.state && (location.state as any).lesson ? (location.state as any).lesson as Lesson : null;
  const trackData = location.state && (location.state as any).track ? (location.state as any).track as TrackDetails : null;
  const sectionTitle = location.state && (location.state as any).sectionTitle ? (location.state as any).sectionTitle : '';

  const currentTrackData = trackData;
  const currentLessonData = lessonData || currentTrackData?.sections[0]?.lessons[0];

  // Verificar se o usuário está inscrito na trilha
  React.useEffect(() => {
    const checkSubscription = async () => {
      if (!currentTrackData?.id) {
        setIsCheckingSubscription(false);
        return;
      }

      try {
        const myLearningTracks = await trackService.getMyLearning();
        
        const isSubscribed = myLearningTracks.rows?.some(track => track.id === currentTrackData.id);
        
        if (isSubscribed) {
          navigate('/hub/learning');
          return;
        }
      } catch (error) {
        console.error('Erro ao verificar inscrição:', error);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [currentTrackData?.id, navigate]);

  const goBack = () => {
    if (currentTrackData) {
      navigate(`/hub/available-tracks/details/${currentTrackData.id}`, { 
        state: { track: currentTrackData } 
      });
    }
  };

  const navigateToLesson = (lesson: Lesson) => {
    if (!currentTrackData) return;
    
    const section = currentTrackData.sections.find(s => 
      s.lessons.some(l => l.id === lesson.id)
    );
    navigate(`/hub/available-tracks/details/lesson/${lesson.id}`, { 
      state: { 
        lesson: lesson, 
        track: currentTrackData,
        sectionTitle: section?.title || ''
      } 
    });
  };

  const getLessonStatus = (lesson: Lesson) => {
    if (lesson.index === 6) return "em-progresso";
    if (lesson.index <= 5) return "concluida";
    return "nao-iniciada";
  };

  const getProgressPercentage = () => {
    return 50;
  };

  const getCompletedLessons = () => {
    return 5;
  };

  if (isCheckingSubscription) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-3">
          <Col>
            <PageTitle>Verificando acesso...</PageTitle>
          </Col>
        </Row>
        <Loading />
      </Wrapper>
    );
  }

  if (!currentTrackData || !currentLessonData) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-3">
          <Col>
            <Button
              variant="outline-secondary"
              onClick={goBack}
              className="mb-3"
            >
              <FontAwesomeIcon icon={["fas", "arrow-left"]} className="me-2" />
              {trans("Voltar")}
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="text-center">
              <h2>{trans("Dados não encontrados")}</h2>
              <p className="text-muted">
                {trans("Os dados da trilha ou aula não foram encontrados")}
              </p>
            </div>
          </Col>
        </Row>
      </Wrapper>
    );
  }

  return (
    <Wrapper>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px' }}>
        <div style={{ flex: '1', maxWidth: 'calc(100% - 416px)', paddingRight: '24px' }}>
          <div className="mb-4">
            <h1 className="h3 fw-bold">Aula {currentLessonData.index} - {currentLessonData.title}</h1>
          </div>
          <div className="mb-4">
            <div 
              className="bg-dark text-white position-relative" 
              style={{ height: '500px', borderRadius: '12px', overflow: 'hidden' }}
            >
              {currentLessonData.videoId ? (
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  {/* YouTube Embed */}
                  {currentLessonData.videoId.startsWith('yt_') ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${currentLessonData.videoId.replace('yt_', '')}?rel=0&modestbranding=1&showinfo=0`}
                      title={currentLessonData.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: '12px'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      controls
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      poster={currentTrackData?.imageUrl}
                    >
                      <source src={currentLessonData.videoId} type="video/mp4" />
                      <source src={currentLessonData.videoId} type="video/webm" />
                      Seu navegador não suporta o elemento de vídeo.
                    </video>
                  )}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
              <div className="text-center">
                    <FontAwesomeIcon 
                      icon={["fas", "play-circle"]} 
                      size="4x" 
                      className="mb-3 text-warning" 
                      style={{ opacity: 0.7 }}
                    />
                    <h4 className="mb-2">Vídeo da Aula</h4>
                    <p className="text-muted mb-0">
                      {currentLessonData.videoId ? 'Carregando vídeo...' : 'Vídeo não disponível'}
                    </p>
                    {!currentLessonData.videoId && (
                      <small className="text-muted d-block mt-2">
                        Esta aula ainda não possui vídeo associado
                      </small>
                    )}
                  </div>
                </div>
              )}
              
              <div 
                className="position-absolute"
                style={{
                  bottom: '20px',
                  left: '20px',
                  right: '20px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="text-white mb-1">
                      Aula {currentLessonData.index} - {currentLessonData.title}
                    </h6>
                    <small className="text-light">
                      <FontAwesomeIcon icon={["fas", "clock"]} className="me-1" />
                      {currentLessonData.minutes} minutos
                    </small>
                  </div>
                  <div className="text-end">
                    <small className="text-light d-block">
                      {currentTrackData?.title}
                    </small>
                    <small className="text-muted">
                      {currentTrackData?.sections?.length || 0} seções
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <style>
              {`
                .custom-tab {
                  border-bottom: 1px solid #dee2e6;
                  margin-bottom: 20px;
                }
                .custom-tab-item {
                  display: inline-block;
                  padding: 12px 20px;
                  cursor: pointer;
                  border-bottom: 3px solid transparent;
                  color: #6c757d;
                  font-weight: 500;
                  transition: all 0.3s ease;
                }
                .custom-tab-item:hover {
                  color: #1365A3;
                  border-bottom: 3px solid #1365A3;
                }
                .custom-tab-item.active {
                  color: #1365A3;
                  border-bottom: 3px solid #1365A3;
                  font-weight: bold;
                }
              `}
            </style>
            <div className="custom-tab">
              <div 
                className={`custom-tab-item ${activeTab === "visao-geral" ? "active" : ""}`}
                onClick={() => setActiveTab("visao-geral")}
              >
                Visão Geral
              </div>
              <div 
                className={`custom-tab-item ${activeTab === "materiais" ? "active" : ""}`}
                onClick={() => setActiveTab("materiais")}
              >
                Materiais de apoio
              </div>
              <div 
                className={`custom-tab-item ${activeTab === "perguntas" ? "active" : ""}`}
                onClick={() => setActiveTab("perguntas")}
              >
                Perguntas
              </div>
              <div 
                className={`custom-tab-item ${activeTab === "enviar-arquivo" ? "active" : ""}`}
                onClick={() => setActiveTab("enviar-arquivo")}
              >
                Enviar arquivo
              </div>
            </div>
          </div>
          <div>
            {activeTab === "visao-geral" && (
              <div className="p-4 bg-white rounded-3 shadow-sm">
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">
                    <FontAwesomeIcon icon={["fas", "info-circle"]} className="me-2 text-primary" />
                    Sobre esta aula
                  </h5>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={["fas", "clock"]} className="me-2 text-muted" />
                        <span className="text-muted">Duração:</span>
                        <span className="ms-2 fw-medium">{currentLessonData.minutes} minutos</span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={["fas", "list-ol"]} className="me-2 text-muted" />
                        <span className="text-muted">Sequência:</span>
                        <span className="ms-2 fw-medium">Aula {currentLessonData.index}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Descrição da Aula</h6>
                  <div className="bg-light p-3 rounded-2">
                    <p className="mb-0">
                      {currentLessonData.description || 
                        "Esta aula faz parte da trilha de aprendizado e aborda conceitos importantes para seu desenvolvimento. " +
                        "Assista ao vídeo com atenção e pratique os exercícios propostos."
                      }
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Objetivos de Aprendizagem</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2 text-success" />
                      Compreender os conceitos fundamentais apresentados
                    </li>
                    <li className="mb-2">
                      <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2 text-success" />
                      Aplicar o conhecimento em situações práticas
                    </li>
                    <li className="mb-2">
                      <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2 text-success" />
                      Desenvolver habilidades específicas da área
                    </li>
                  </ul>
                </div>

                <div className="alert alert-info">
                  <FontAwesomeIcon icon={["fas", "lightbulb"]} className="me-2" />
                  <strong>Dica:</strong> Para melhor aproveitamento, assista ao vídeo completo e faça anotações dos pontos principais.
                </div>

                {/* Navegação entre aulas */}
                <div className="mt-4 pt-4 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        const currentIndex = currentTrackData?.sections?.findIndex(s => 
                          s.lessons.some(l => l.id === currentLessonData.id)
                        );
                        if (currentIndex !== undefined && currentIndex > 0) {
                          const prevSection = currentTrackData?.sections?.[currentIndex - 1];
                          const prevLesson = prevSection?.lessons?.[prevSection.lessons.length - 1];
                          if (prevLesson) {
                            navigateToLesson(prevLesson);
                          }
                        }
                      }}
                      disabled={!currentTrackData?.sections?.some(s => 
                        s.lessons.some(l => l.id === currentLessonData.id)
                      )}
                    >
                      <FontAwesomeIcon icon={["fas", "chevron-left"]} className="me-2" />
                      Aula Anterior
                    </button>
                    
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        const currentIndex = currentTrackData?.sections?.findIndex(s => 
                          s.lessons.some(l => l.id === currentLessonData.id)
                        );
                        if (currentIndex !== undefined && currentIndex < (currentTrackData?.sections?.length || 0) - 1) {
                          const nextSection = currentTrackData?.sections?.[currentIndex + 1];
                          const nextLesson = nextSection?.lessons?.[0];
                          if (nextLesson) {
                            navigateToLesson(nextLesson);
                          }
                        }
                      }}
                      disabled={!currentTrackData?.sections?.some(s => 
                        s.lessons.some(l => l.id === currentLessonData.id)
                      )}
                    >
                      Próxima Aula
                      <FontAwesomeIcon icon={["fas", "chevron-right"]} className="ms-2" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "materiais" && (
              <div className="p-4 bg-white rounded-3 shadow-sm">
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">
                    <FontAwesomeIcon icon={["fas", "folder-open"]} className="me-2 text-primary" />
                    Materiais de Apoio
                  </h5>
                  <p className="text-muted">Arquivos e recursos complementares para esta aula.</p>
                </div>
                
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={["fas", "folder-open"]} className="text-muted mb-3" size="3x" />
                  <h6 className="text-muted">Nenhum material disponível</h6>
                  <p className="text-muted small">Os materiais de apoio serão disponibilizados pelo instrutor.</p>
                </div>
              </div>
            )}

            {activeTab === "perguntas" && (
              <div className="p-4 bg-white rounded-3 shadow-sm">
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">
                    <FontAwesomeIcon icon={["fas", "question-circle"]} className="me-2 text-primary" />
                    Perguntas
                  </h5>
                  <p className="text-muted">Faça perguntas sobre esta aula.</p>
                </div>
                
                <div className="mb-4">
                  <div className="text-center py-4">
                    <FontAwesomeIcon icon={["fas", "comments"]} className="text-muted mb-3" size="2x" />
                    <h6 className="text-muted">Nenhuma pergunta enviada</h6>
                    <p className="text-muted small">Seja o primeiro a fazer uma pergunta sobre esta aula.</p>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="questionInput" className="form-label fw-medium">Fazer uma pergunta</label>
                    <textarea 
                      id="questionInput"
                      className="form-control" 
                      rows={4}
                      placeholder="Digite uma pergunta"
                      style={{ resize: 'vertical' }}
                    />
                    <div className="d-flex justify-content-end mt-3">
                      <button className="btn btn-primary">
                        <FontAwesomeIcon icon={["fas", "paper-plane"]} className="me-2" />
                        Enviar pergunta
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "enviar-arquivo" && (
              <div className="p-4 bg-white rounded-3 shadow-sm">
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">
                    <FontAwesomeIcon icon={["fas", "upload"]} className="me-2 text-primary" />
                    Enviar arquivo
                  </h5>
                  <p className="text-muted">Envie arquivos relacionados a esta aula.</p>
                </div>
                
                <div className="mb-4">
                  <div className="border border-dashed rounded-3 p-5 text-center" style={{ borderColor: '#dee2e6' }}>
                    <FontAwesomeIcon icon={["fas", "cloud-upload-alt"]} className="text-muted mb-3" size="3x" />
                    <h6 className="text-muted">Arraste e solte arquivos aqui</h6>
                    <p className="text-muted small mb-3">ou</p>
                    <button className="btn btn-outline-primary">
                      <FontAwesomeIcon icon={["fas", "folder-open"]} className="me-2" />
                      Selecionar arquivos
                    </button>
                  </div>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      <FontAwesomeIcon icon={["fas", "info-circle"]} className="me-1" />
                      Formatos aceitos: PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX (máx. 10MB)
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div 
          className="bg-white shadow-sm position-fixed"
          style={{ 
            width: '392px',
            height: 'calc(100vh - 80px)',
            top: '80px',
            right: '0px',
            paddingTop: '32px',
            paddingBottom: '32px',
            paddingLeft: '24px',
            paddingRight: '24px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          <div className="text-center mb-4">
            <div className="d-flex align-items-center justify-content-center mb-3">
              <h6 className="mb-0 fw-bold me-2">Aulas</h6>
            </div>
            
            <div className="position-relative mb-4" style={{ marginTop: '40px' }}>
              <div 
                className="position-absolute"
                style={{
                  left: `${getProgressPercentage()}%`,
                  transform: 'translateX(-50%)',
                  top: '-35px',
                  backgroundColor: '#c5e6ff',
                  color: '#1365A3',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  zIndex: 10
                }}
              >
                {getProgressPercentage()}%
                <div 
                  className="position-absolute"
                  style={{
                    bottom: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '4px solid #c5e6ff'
                  }}
                ></div>
              </div>
              <div className="progress" style={{ width: '100%', height: '6px', backgroundColor: '#e9ecef' }}>
                <div 
                  className="progress-bar" 
                  role="progressbar" 
                  style={{ 
                    width: `${getProgressPercentage()}%`,
                    backgroundColor: '#c5e6ff'
                  }}
                  aria-valuenow={getProgressPercentage()} 
                  aria-valuemin={0} 
                  aria-valuemax={100}
                ></div>
              </div>
            </div>
            
            <small className="text-muted">{getCompletedLessons()}/{currentTrackData?.totalLessons || 0} aulas</small>
          </div>
            <div 
              className="flex-grow-1 overflow-auto"
              style={{ 
                maxHeight: 'calc(100vh - 300px)',
                overflowY: 'auto'
              }}
            >
            {currentTrackData?.sections?.map((s, sectionIndex) => (
              <div key={s.id}>
                {s.lessons.map((l, index) => {
                  const status = getLessonStatus(l);
                  const isLastLessonOfLastSection = sectionIndex === (currentTrackData?.sections?.length || 0) - 1 && index === s.lessons.length - 1;
                  return (
                    <div 
                      key={l.id} 
                      onClick={() => navigateToLesson(l)}
                      style={{ 
                        cursor: 'pointer',
                        padding: '16px 0',
                        borderBottom: isLastLessonOfLastSection ? 'none' : '1px solid #C4C5C6'
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div className="flex-grow-1">
                          {status === "em-progresso" && (
                            <span 
                              className="badge mb-2" 
                              style={{ 
                                fontSize: '10px',
                                backgroundColor: '#FFE7D1',
                                color: '#A95100',
                                borderRadius: '4px',
                                padding: '4px 8px'
                              }}
                            >
                              {trans("Em progresso")}
                            </span>
                          )}
                          {status === "concluida" && (
                            <span 
                              className="badge mb-2" 
                              style={{ 
                                fontSize: '10px',
                                backgroundColor: '#C4C5C6',
                                color: '#464646',
                                borderRadius: '4px',
                                padding: '4px 8px'
                              }}
                            >
                              {trans("Concluída")}
                            </span>
                          )}
                          {status === "nao-iniciada" && (
                            <span 
                              className="badge mb-2" 
                              style={{ 
                                fontSize: '10px',
                                backgroundColor: '#C4C5C6',
                                color: '#464646',
                                borderRadius: '4px',
                                padding: '4px 8px'
                              }}
                            >
                              {trans("Não iniciada")}
                            </span>
                          )}
                          <div className="title fw-bold mb-1">{l.title}</div>
                          <div className="subline d-flex align-items-center">
                            <img 
                              src="/video-library.svg" 
                              alt="Video Library" 
                              style={{ width: '14px', height: '14px', marginRight: '4px' }}
                            />
                            <span>{trans("Aula")} {l.index}</span>
                            <span className="mx-1">•</span>
                            <span>{l.minutes} {trans("minutos")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};
